import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { z } from "zod";

import { supabase } from "@/integrations/supabase/client";
import { cancelMySubscription, getMyDashboard, openBillingPortal } from "@/lib/dashboard.functions";
import { requestPasswordReset } from "@/lib/auth.functions";

const DashboardSearch = z.object({
  redirect: z.string().optional(),
  checkout: z.enum(["success"]).optional(),
});


export const Route = createFileRoute("/dashboard")({
  ssr: false,
  validateSearch: DashboardSearch,
  // Resolve auth before the component renders → no loading flash, no flicker
  // between SignInView and SignedInDashboard.
  beforeLoad: async () => {
    const { data } = await supabase.auth.getUser();
    return { initialAuthed: !!data.user };
  },
  component: DashboardPage,
  head: () => ({
    meta: [
      { title: "Your dashboard · the Vibing Company®" },
      {
        name: "description",
        content:
          "Track your project status, purchases, and active engagements with the Vibing Company.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const STATUS_COPY: Record<string, { label: string; sub: string; tone: string }> = {
  reviewing: {
    label: "We're reviewing your request",
    sub: "Sit tight — the team is looking at your brief and will be in touch shortly.",
    tone: "bg-amber-500/10 text-amber-600 ring-amber-500/20",
  },
  onboarding: {
    label: "Onboarding in progress",
    sub: "We're spinning up your project, setting up access, and aligning on scope.",
    tone: "bg-sky-500/10 text-sky-600 ring-sky-500/20",
  },
  active: {
    label: "Active engagement",
    sub: "Your engagement is live. Expect regular updates from the team.",
    tone: "bg-emerald-500/10 text-emerald-600 ring-emerald-500/20",
  },
  paused: {
    label: "Paused",
    sub: "Your engagement is on hold. Ping us when you're ready to resume.",
    tone: "bg-foreground/10 text-foreground/70 ring-foreground/20",
  },
  completed: {
    label: "Completed",
    sub: "Wrapped. Thank you — we'd love to work together again.",
    tone: "bg-emerald-500/10 text-emerald-600 ring-emerald-500/20",
  },
};

function formatMoney(cents: number | null | undefined, currency: string | null | undefined) {
  if (cents == null) return "—";
  const sym = (currency ?? "usd").toLowerCase() === "eur" ? "€" : "$";
  return `${sym}${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
}
function formatDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function safeRedirect(raw: string | undefined): string | null {
  if (!raw) return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
}

function DashboardPage() {
  const { initialAuthed } = Route.useRouteContext();
  const search = Route.useSearch();
  const router = useRouter();
  const [authed, setAuthed] = useState<boolean>(initialAuthed);

  useEffect(() => {
    // Honor ?redirect= after a successful sign-in (e.g. coming from /admin).
    if (authed) {
      const dest = safeRedirect(search.redirect);
      if (dest && dest !== "/dashboard") {
        router.navigate({ to: dest, replace: true });
      }
    }
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      const next = !!session?.user;
      setAuthed(next);
      if (event === "SIGNED_IN") {
        const dest = safeRedirect(search.redirect);
        if (dest && dest !== "/dashboard") {
          router.navigate({ to: dest, replace: true });
        }
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [authed, router, search.redirect]);

  return authed ? <SignedInDashboard /> : <SignInView />;
}



function SignInView() {
  const requestReset = useServerFn(requestPasswordReset);
  const [mode, setMode] = useState<"signin" | "forgot">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState<
    "idle" | "signing_in" | "sending" | "sent" | "not_found" | "error"
  >("idle");
  const [message, setMessage] = useState<string | null>(null);

  const signIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setState("signing_in");
    setMessage(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setState("error");
      const lower = error.message.toLowerCase();
      setMessage(
        lower.includes("invalid")
          ? "That email and password don't match. If this is your first sign-in, set a password below."
          : error.message,
      );
      return;
    }
    setState("idle");
    // onAuthStateChange in the parent will flip to SignedInDashboard.
  };

  const sendReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setState("sending");
    setMessage(null);
    try {
      const res = await requestReset({
        data: { email, redirectTo: `${window.location.origin}/reset-password` },
      });
      if (res.ok) setState("sent");
      else if (res.reason === "not_found") {
        setState("not_found");
        setMessage(res.message);
      } else {
        setState("error");
        setMessage(res.message);
      }
    } catch (err) {
      setState("error");
      setMessage(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  const inSent = state === "sent";

  return (
    <div className="min-h-dvh bg-background flex items-center justify-center px-6">
      <main id="main" tabIndex={-1} className="w-full max-w-md">
        <Link
          to="/"
          className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground hover:text-foreground"
        >
          ← the Vibing Company®
        </Link>
        <h1 className="mt-8 font-display text-5xl tracking-tight text-foreground leading-[1.05]">
          {mode === "forgot" ? "Reset your password" : "Sign in to your project"}
        </h1>
        <p className="mt-4 text-muted-foreground text-base leading-relaxed">
          {mode === "forgot"
            ? "Enter the email your invitation was sent to. We'll email a link to set a new password."
            : "Dashboard access is by invitation only. Sign in with the email your invite was sent to."}
        </p>

        {inSent ? (
          <div className="mt-10 rounded-2xl border border-foreground/10 bg-white p-6">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              Check your inbox
            </p>
            <p className="mt-3 text-foreground">
              We sent a password reset link to <span className="font-medium">{email}</span>.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Click the link from this device to set your password and sign in.
            </p>
            <button
              type="button"
              onClick={() => {
                setState("idle");
                setMode("signin");
              }}
              className="mt-6 text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
            >
              ← Back to sign in
            </button>
          </div>
        ) : mode === "forgot" ? (
          <form onSubmit={sendReset} className="mt-10 space-y-4">
            <div>
              <label htmlFor="reset-email" className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                Email
              </label>
              <input
                id="reset-email"
                name="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                required
                autoFocus
                aria-invalid={state === "error" || state === "not_found" || undefined}
                aria-describedby={message ? "reset-email-status" : undefined}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-2 w-full rounded-xl border border-foreground/15 bg-white px-4 py-3.5 text-foreground placeholder:text-foreground/30 focus:outline-none focus:ring-2 focus:ring-foreground/30"
              />
            </div>
            <button
              type="submit"
              disabled={state === "sending"}
              className="w-full rounded-xl bg-foreground text-background px-4 py-3.5 text-sm font-medium tracking-wide [transition-property:opacity,transform] duration-200 hover:opacity-90 active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100"
            >
              {state === "sending" ? "Sending…" : "Send reset link"}
            </button>
            <div aria-live="polite" id="reset-email-status">
              {state === "not_found" && message && (
                <p className="text-sm text-amber-700 bg-amber-500/10 rounded-lg p-3">{message}</p>
              )}
              {state === "error" && message && (
                <p className="text-sm text-destructive">Error: {message}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setMode("signin");
                setState("idle");
                setMessage(null);
              }}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
            >
              ← Back to sign in
            </button>
          </form>
        ) : (
          <form onSubmit={signIn} className="mt-10 space-y-4">
            <div>
              <label htmlFor="signin-email" className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                Email
              </label>
              <input
                id="signin-email"
                name="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-2 w-full rounded-xl border border-foreground/15 bg-white px-4 py-3.5 text-foreground placeholder:text-foreground/30 focus:outline-none focus:ring-2 focus:ring-foreground/30"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="signin-password" className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setMode("forgot");
                    setState("idle");
                    setMessage(null);
                  }}
                  className="text-[10px] font-mono tracking-[0.15em] uppercase text-muted-foreground hover:text-foreground underline underline-offset-4"
                >
                  Forgot / set password
                </button>
              </div>
              <input
                id="signin-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                aria-invalid={state === "error" || undefined}
                aria-describedby={state === "error" && message ? "signin-status" : undefined}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 w-full rounded-xl border border-foreground/15 bg-white px-4 py-3.5 text-foreground placeholder:text-foreground/30 focus:outline-none focus:ring-2 focus:ring-foreground/30"
              />
            </div>
            <button
              type="submit"
              disabled={state === "signing_in"}
              className="w-full rounded-xl bg-foreground text-background px-4 py-3.5 text-sm font-medium tracking-wide [transition-property:opacity,transform] duration-200 hover:opacity-90 active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100"
            >
              {state === "signing_in" ? "Signing in…" : "Sign in"}
            </button>
            <div aria-live="assertive" id="signin-status">
              {state === "error" && message && (
                <p className="text-sm text-destructive">Error: {message}</p>
              )}
            </div>
            <p className="text-xs text-muted-foreground pt-2">
              No invitation yet?{" "}
              <Link to="/contact" className="underline underline-offset-4 hover:text-foreground">
                Get in touch →
              </Link>
            </p>
          </form>
        )}
      </main>
    </div>
  );
}

function SignedInDashboard() {
  const fetchDashboard = useServerFn(getMyDashboard);
  const openPortal = useServerFn(openBillingPortal);
  const cancelSub = useServerFn(cancelMySubscription);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { checkout } = Route.useSearch();
  const { data, isLoading, error } = useQuery({
    queryKey: ["my-dashboard"],
    queryFn: () => fetchDashboard(),
    refetchOnWindowFocus: true,
  });

  // After a successful checkout, refetch dashboard data so the new
  // subscription/charge shows up, then strip the ?checkout=success param.
  useEffect(() => {
    if (checkout !== "success") return;
    qc.invalidateQueries({ queryKey: ["my-dashboard"] });
    const t = window.setTimeout(() => {
      navigate({ to: "/dashboard", replace: true, search: {} });
    }, 6000);
    return () => window.clearTimeout(t);
  }, [checkout, navigate, qc]);


  const portalMutation = useMutation({
    mutationFn: async () => {
      const res = await openPortal({ data: { returnUrl: window.location.href } });
      if ("error" in res) throw new Error(res.error);
      return res.url;
    },
    onSuccess: (url) => {
      if (url) window.location.href = url;
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (subscriptionId: string) => {
      const res = await cancelSub({ data: { subscriptionId } });
      if ("error" in res) throw new Error(res.error);
      return res;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-dashboard"] }),
  });

  if (isLoading) {
    return (
      <div className="min-h-dvh bg-background flex items-center justify-center" role="status" aria-live="polite">
        <p className="text-muted-foreground font-mono text-xs tracking-widest uppercase">Loading your dashboard…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-dvh bg-background flex items-center justify-center px-6" role="alert">
        <p className="text-destructive">Could not load your dashboard.</p>
      </div>
    );
  }

  const profile = data?.profile;
  const status = data?.status ?? { value: "reviewing" as const, note: null, updated_at: null };
  const statusCopy = STATUS_COPY[status.value] ?? STATUS_COPY.reviewing;
  const subscriptions = data?.stripe.subscriptions ?? [];
  const charges = data?.stripe.charges ?? [];
  const pendingQuote = data?.pending_quote ?? null;

  return (
    <div className="min-h-dvh bg-background">
      <main id="main" tabIndex={-1} className="mx-auto max-w-4xl px-6 py-12 md:py-20">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground hover:text-foreground"
          >
            ← the Vibing Company®
          </Link>
          <button
            onClick={async () => {
              await supabase.auth.signOut();
              qc.clear();
            }}
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
          >
            Sign out
          </button>
        </div>

        {checkout === "success" && (
          <div role="status" className="mt-8 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 px-5 py-4 text-sm text-emerald-700">
            Payment received — welcome aboard. Your engagement details will appear here shortly.
          </div>
        )}

        <h1 className="mt-12 font-display text-5xl md:text-6xl tracking-tight text-foreground leading-[1.02]">
          {profile?.name ? `Hey ${profile.name.split(" ")[0]}.` : "Welcome back."}
        </h1>
        <p className="mt-3 text-foreground/55">{profile?.email}</p>


        {/* Status card */}
        <div className="mt-12 rounded-3xl border border-foreground/10 bg-white p-8 md:p-10">
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ring-1 ${statusCopy.tone}`}
            >
              <span className="size-1.5 rounded-full bg-current" />
              {status.value}
            </span>
            {status.updated_at && (
              <span className="font-mono text-[10px] tracking-widest uppercase text-foreground/40">
                Updated {formatDate(status.updated_at)}
              </span>
            )}
          </div>
          <h2 className="mt-5 font-display text-3xl md:text-4xl tracking-tight text-foreground leading-tight">
            {statusCopy.label}
          </h2>
          <p className="mt-3 text-foreground/60 max-w-prose">{status.note ?? statusCopy.sub}</p>
        </div>

        {/* Pending quote — show Pay Now when admin has confirmed a price */}
        {pendingQuote && subscriptions.length === 0 && (
          <div className="mt-8 rounded-3xl border border-foreground/15 bg-foreground text-background p-8 md:p-10">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-background/60">
              Your proposal
            </p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl tracking-tight leading-tight">
              {formatMoney(pendingQuote.amount_cents, pendingQuote.currency)} — ready when you are
            </h2>
            <p className="mt-3 text-background/70 max-w-prose">
              Complete payment to kick off your engagement. You'll be redirected to a secure Stripe checkout.
            </p>
            <div className="mt-6 flex items-center gap-4 flex-wrap">
              {pendingQuote.payment_link_url ? (
                <a
                  href={pendingQuote.payment_link_url}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center rounded-full bg-background text-foreground px-6 py-3 text-sm font-medium tracking-wide hover:opacity-90 [transition-property:opacity,transform] duration-200 active:scale-[0.98]"
                >
                  Pay now →
                </a>
              ) : (
                <p className="text-sm text-background/70">
                  Payment link is being prepared — refresh in a moment or reach out if it doesn't appear.
                </p>
              )}
              <Link
                to="/contact"
                className="text-xs text-background/60 hover:text-background underline underline-offset-4"
              >
                Questions? Contact us →
              </Link>
            </div>
          </div>
        )}

        {/* Subscription */}
        <section className="mt-10">
          <div className="flex items-end justify-between">
            <h3 className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              Subscription
            </h3>
            {data?.stripe.customer_id && subscriptions.length > 0 && (
              <button
                onClick={() => portalMutation.mutate()}
                disabled={portalMutation.isPending}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4 disabled:opacity-50"
              >
                {portalMutation.isPending ? "Opening…" : "Manage billing →"}
              </button>
            )}
          </div>
          {subscriptions.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">No active subscription.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {subscriptions.map((s) => {
                const canCancel =
                  !s.cancel_at_period_end &&
                  (s.status === "active" || s.status === "trialing" || s.status === "past_due");
                return (
                  <li
                    key={s.id}
                    className="rounded-2xl border border-foreground/10 bg-white p-5"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <p className="font-display text-xl tracking-tight text-foreground capitalize">
                          {s.plan_name?.replace(/_/g, " ") ?? "Plan"}
                        </p>
                        <p className="text-xs text-foreground/55 mt-1">
                          {formatMoney(s.amount, s.currency)}
                          {s.interval ? ` / ${s.interval}` : ""} ·{" "}
                          {s.cancel_at_period_end
                            ? `Ends ${formatDate(s.current_period_end)}`
                            : `Renews ${formatDate(s.current_period_end)}`}
                        </p>
                      </div>
                      <span
                        className={`text-[11px] uppercase tracking-wider font-mono ${
                          s.status === "active" || s.status === "trialing"
                            ? "text-emerald-600"
                            : s.status === "past_due"
                              ? "text-amber-600"
                              : "text-foreground/50"
                        }`}
                      >
                        {s.cancel_at_period_end ? "ending" : s.status}
                      </span>
                    </div>
                    {canCancel && (
                      <div className="mt-4 pt-4 border-t border-foreground/10 flex items-center justify-end">
                        <button
                          onClick={() => {
                            if (window.confirm(`Cancel this subscription? You'll keep access until ${formatDate(s.current_period_end)}.`)) {
                              cancelMutation.mutate(s.id);
                            }
                          }}
                          disabled={cancelMutation.isPending}
                          className="text-xs text-foreground/60 hover:text-destructive underline underline-offset-4 disabled:opacity-50"
                        >
                          {cancelMutation.isPending && cancelMutation.variables === s.id
                            ? "Cancelling…"
                            : "Cancel subscription"}
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {cancelMutation.isError && (
            <p className="mt-3 text-xs text-destructive">{(cancelMutation.error as Error).message}</p>
          )}
        </section>

        {/* Purchases */}
        <section className="mt-10">
          <h3 className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
            Purchases
          </h3>
          {charges.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">No purchases yet.</p>
          ) : (
            <ul className="mt-4 divide-y divide-foreground/10 rounded-2xl border border-foreground/10 bg-white overflow-hidden">
              {charges.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between gap-4 px-5 py-4"
                >
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {c.description ?? "Purchase"}
                    </p>
                    <p className="text-xs text-foreground/55">{formatDate(c.created)}</p>
                  </div>
                  <div className="text-right flex items-center gap-4">
                    <span className="font-mono text-sm text-foreground">
                      {formatMoney(c.amount, c.currency)}
                    </span>
                    {c.receipt_url && (
                      <a
                        href={c.receipt_url}
                        target="_blank"
                        rel="noopener"
                        className="text-[11px] uppercase tracking-wider text-foreground/50 hover:text-foreground underline underline-offset-4"
                      >
                        Receipt
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Contact */}
        <section className="mt-10 grid sm:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-foreground/10 bg-white p-6">
            <h3 className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              Contact on file
            </h3>
            <p className="mt-3 text-foreground">{profile?.name ?? "—"}</p>
            <p className="text-sm text-foreground/60">{profile?.email}</p>
          </div>
          <div className="rounded-2xl border border-foreground/10 bg-white p-6">
            <h3 className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              Talk to us
            </h3>
            <p className="mt-3 text-sm text-foreground/60">
              Questions, scope changes, anything else — we're one message away.
            </p>
            <div className="mt-4 flex gap-4 text-sm">
              <Link
                to="/contact"
                className="text-foreground underline underline-offset-4 hover:opacity-70"
              >
                Contact page →
              </Link>
              <a
                href="mailto:hi@vibing.inc"
                className="text-foreground underline underline-offset-4 hover:opacity-70"
              >
                hi@vibing.inc
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
